import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { PageHeader, PageBody, EmptyState } from "../components/Shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Link } from "react-router-dom";
import { Plus, Search, Edit2, Trash2 } from "lucide-react";
import { toast } from "sonner";

const emptyForm = { name: "", contact_person: "", email: "", phone: "", address: "", tax_no: "", notes: "" };

function CustomerFormDialog({ open, onOpenChange, initial, onSave, mode }) {
  const [form, setForm] = useState(emptyForm);
  useEffect(() => { if (open) setForm({ ...emptyForm, ...(initial || {}) }); }, [open, initial]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await onSave(form);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || `Failed to ${mode} customer`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-none">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            {mode === "edit" ? "Edit Customer" : "New Customer"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {[
            ["name", "Name", true],
            ["contact_person", "Primary contact"],
            ["email", "Email"],
            ["phone", "Phone"],
            ["tax_no", "Tax No."],
          ].map(([k, label, req]) => (
            <div key={k}>
              <Label className="label-caps">{label}</Label>
              <Input
                data-testid={`customer-${k}`}
                className="rounded-none mt-1"
                required={!!req}
                value={form[k] || ""}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
              />
            </div>
          ))}
          <div>
            <Label className="label-caps">Address</Label>
            <Textarea data-testid="customer-address" className="rounded-none mt-1" rows={2} value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          </div>
          <div>
            <Label className="label-caps">Notes</Label>
            <Textarea data-testid="customer-notes" className="rounded-none mt-1" rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <DialogFooter>
            <Button type="submit" className="rounded-none bg-blue-600 hover:bg-blue-700" data-testid="customer-save-btn">
              {mode === "edit" ? "Save changes" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Customers() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/customers").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const filtered = items.filter((c) =>
    !q ||
    c.name?.toLowerCase().includes(q.toLowerCase()) ||
    c.tax_no?.toLowerCase().includes(q.toLowerCase()) ||
    c.email?.toLowerCase().includes(q.toLowerCase())
  );

  const create = async (form) => {
    await api.post("/customers", form);
    toast.success("Customer created");
    load();
  };
  const save = async (form) => {
    await api.put(`/customers/${editing.id}`, form);
    toast.success("Customer updated");
    setEditing(null);
    load();
  };
  const del = async (c) => {
    if (!window.confirm(`Delete customer ${c.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/customers/${c.id}`);
      toast.success("Customer deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  return (
    <>
      <PageHeader
        title="Customers"
        subtitle="Directory"
        testid="customers-title"
        actions={
          <Button
            className="rounded-none bg-blue-600 hover:bg-blue-700 text-white"
            onClick={() => setCreateOpen(true)}
            data-testid="new-customer-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> New Customer
          </Button>
        }
      />
      <PageBody>
        <div className="mb-4 flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search customers…"
              className="rounded-none pl-9 w-80"
              data-testid="customers-search"
            />
          </div>
          <div className="label-caps ml-auto">{filtered.length} results</div>
        </div>

        {filtered.length === 0 ? (
          <EmptyState title="No customers yet" hint="Create your first customer to get started." />
        ) : (
          <div className="bg-white border border-slate-300">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-300">
                <tr className="text-left">
                  <th className="py-2 px-3 label-caps">Name</th>
                  <th className="py-2 px-3 label-caps">Contact</th>
                  <th className="py-2 px-3 label-caps">Email</th>
                  <th className="py-2 px-3 label-caps">Phone</th>
                  <th className="py-2 px-3 label-caps">Tax No.</th>
                  <th className="py-2 px-3 w-24 text-right label-caps">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.id} className="border-b border-slate-200 hover:bg-slate-50 stagger-row" data-testid={`customer-row-${c.id}`}>
                    <td className="py-2 px-3">
                      <Link to={`/customers/${c.id}`} className="text-blue-700 hover:text-blue-900 font-medium">{c.name}</Link>
                      {(c.contacts?.length || 0) > 0 && (
                        <span className="ml-2 text-xs font-mono text-slate-400">+{c.contacts.length} contact{c.contacts.length === 1 ? "" : "s"}</span>
                      )}
                    </td>
                    <td className="py-2 px-3">{c.contact_person || "-"}</td>
                    <td className="py-2 px-3 font-mono text-slate-600">{c.email || "-"}</td>
                    <td className="py-2 px-3 font-mono text-slate-600">{c.phone || "-"}</td>
                    <td className="py-2 px-3 font-mono text-slate-600">{c.tax_no || "-"}</td>
                    <td className="py-2 px-3 text-right">
                      <div className="inline-flex gap-2">
                        <button
                          onClick={() => setEditing(c)}
                          className="text-slate-400 hover:text-blue-700"
                          data-testid={`edit-customer-${c.id}`}
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => del(c)}
                          className="text-slate-400 hover:text-red-600"
                          data-testid={`del-customer-${c.id}`}
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <CustomerFormDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          initial={null}
          onSave={create}
          mode="create"
        />
        <CustomerFormDialog
          open={!!editing}
          onOpenChange={(v) => { if (!v) setEditing(null); }}
          initial={editing}
          onSave={save}
          mode="edit"
        />
      </PageBody>
    </>
  );
}
